import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {
  LinkingOptions,
  NavigationContainer,
  createNavigationContainerRef,
  getStateFromPath,
} from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Lobby } from '../components/screens/Lobby/Lobby';
import { PlayOnline } from '../components/screens/PlayOnline';
import { HostGame } from '../components/screens/HostGame';
import { JoinGame } from '../components/screens/JoinGame';
import { PlayWithAI } from '../components/screens/PlayWithAI';
import { JoinCode } from '../components/screens/JoinCode';
import { Game, GameType, GameMode } from '../components/screens/GameScreen';
import { Splash } from '../components/screens/Splash';
import { AIDifficultyLevel } from '../components/screens/PlayWithAI/useJoinAiGameMutation.gql';
import { Platform, Text, View } from 'react-native';
import { gitBranch } from '../version';
import ForceUpdate from '../components/helperComponents/ForceUpdate';
import { SignIn } from '../components/screens/Login/Login';
import { RequestForgetPasswordCode } from '../components/screens/ForgotPassword/RequestForgetPasswordCode';
import { SignUp } from '../components/screens/SignUp/SignUp';
import { ChooseUserType } from '../components/screens/ChooseUserType/ChooseUserType';
import { USER_TYPES } from '../typesInterfacesEnums/enums';
import { VerifyEmail } from '../components/screens/VerifyEmail/VerifyEmail';
import { SIGNED_IN_STATUS, useSignedInContext } from '../context/SignedInContext';
import { MB_accessTokenUtils } from '@mightybyte/rnw.utils.access-token-utils';
import { VerifyForgetPasswordCode } from '../components/screens/ForgotPassword/VerifyForgetPasswordCode';
import { ResetPassword } from '../components/screens/ForgotPassword/ResetPassword';
import { ResetPasswordSuccessful } from '../components/screens/ForgotPassword/ResetPasswordSuccessful';
import { MyHives } from '../components/screens/MyHives/MyHives';
import { CreateHive } from '../components/screens/CreateHive/CreateHive';
import { SelectHiveSize } from '../components/screens/CreateHive/SelectHiveSize';
import { MyHiveData } from '../components/screens/MyHiveData/MyHiveData';
import { JoinHive } from '../components/screens/JoinHive/JoinHive';
import { OrderSummary } from '../components/screens/CreateHive/OrderSummary';
import { MyHiveDataStudent } from '../components/screens/MyHiveDataStudent/MyHiveDataStudent';
import { SelectGameType } from '../components/screens/SelectGameType/SelectGameType';
import { GAME_TYPES_TYPE, HiveSize, HiveSubscriptionType } from '../constants/constants';
import { useGetCurrentUserData } from '../hooks/user';
import { envs } from '../../env';
import { ServerRedirect } from '../components/screens/ServerRedirect/ServerRedirect';
import { MB_FirebaseMessaging } from '../mightyByteLibraries/MB_FirebaseMessaging/MB_FirebaseMessaging';
import { useAddFirebaseDeviceToken } from '../hooks/addFirebaseDeviceToken';
import {
  MB_EVENT_EMMITER_EVENT_TYPE,
  MB_EventEmmiter,
  TMB_EventSubscription,
} from '@mightybyte/rnw.utils.event-emmiter/MB_EventEmmiter';
import { WaitingForHiveMate } from '../components/screens/WaitingForHiveMate/WaitingForHiveMate';
import { isMobileApp, isMobile } from '@mightybyte/rnw.utils.device-info';
import { InviteToGame } from '../components/helperComponents/InviteToGame/InviteToGame';
import { PassAndPlay } from '../components/screens/PassAndPlay/PassAndPlay';
import { ContactUs } from '../components/screens/ContactUs/ContactUs';
import { AdminNavigator } from './AdminNavigator/AdminNavigator';
import { WebOnly } from '../components/screens/WebOnly';
import { utils } from '../utils/utils';
import { Header } from './Header';
import { Subscriptions } from '../components/screens/Subscriptions/Subscriptions';

import LobbyJourney from '../components/screens/Journey/Journey';
import JourneySurvey from '../components/screens/Journey/Survey/Survey';
import JourneyAssessment from '../components/screens/Journey/Assessment/JourneyAssessment';
import GymAssessment from '../components/screens/Gym/Assessment/GymAssessment';
import NectarNookScreen from '../components/screens/NectarNook/NectarNook';
import { HiveJourneys } from '../components/screens/MyHiveData/HiveJourneys';
import HiveSettings from '../components/screens/HiveSettings/HiveSettings';
import HiveReports from '../components/screens/HiveReports/HiveReports';
import GymStats from '../components/screens/Gym/Stats/GymStats';

export type RootStackParamList = {
  ChooseUserType: undefined | { userType: USER_TYPES };
  VerifyEmail: { firstName: string; lastName: string; password: string; email: string; devCode?: string };
  SignUp: { userType: USER_TYPES };
  SignIn: undefined;
  RequestForgetPasswordCode: undefined;
  VerifyForgetPasswordCode: { email: string };
  ResetPassword: { email: string; code: string };
  ResetPasswordSuccessful: undefined;
  MyHives: undefined | { shouldRefetch: boolean };
  MyHiveData: { id: string };
  MyHiveDataStudent: { id: string; name: string; size: HiveSize };
  SelectHiveSize?: { size: HiveSize };
  CreateHive: { size: HiveSize; subscriptionType: HiveSubscriptionType };
  OrderSummary: { id: string; name: string; size: HiveSize };
  JoinHive: undefined;
  WaitingForHiveMate: {
    userName: string;
    gameType: GAME_TYPES_TYPE;
    inviteeUserId: string;
    inviteeUsername: string;
    hiveDetail: { name: string; id: string; size: HiveSize };
  };
  AdminNavigator: undefined;
  Lobby: undefined | { playAFriend?: boolean; gym?: boolean; hiveId?: string };
  LobbyJourney: { gameType: GAME_TYPES_TYPE; journey: boolean };
  JourneySurvey: undefined;
  JourneyAssessment: undefined;
  GymAssessment:
    | {
        gameType: GAME_TYPES_TYPE;
        gymAssessment: boolean;
      }
    | undefined;
  GymStats: { queryUserId?: string; userName?: string };
  SelectGameType: {
    userName?: string;
    next?:
      | 'PlayOnline'
      | 'HostGame'
      | 'PlayWithAI'
      | 'WaitingForHiveMate'
      | 'PassAndPlay'
      | 'GymAssessment';
    inviteeId?: string;
    inviteeName?: string;
    hiveId?: string;
    hiveDetail?: { name: string; id: string; size: HiveSize };
    journey?: boolean;
    gym?: boolean;
    gymAssessment?: boolean;
  };
  HiveJourneys: { userName: string; userId: string; journey: boolean; hiveId: string };
  HiveSettings: { id: string };
  HiveReports: { id: string };
  NectarNook: undefined;
  ServerRedirect: { success?: string; error?: string } | undefined;
  PlayOnline: { userName?: string; isPlayAgain?: boolean; gameType: GAME_TYPES_TYPE };
  PlayWithAI: {
    userName?: string;
    isPlayAgain?: boolean;
    difficulty?: AIDifficultyLevel;
    gameType: GAME_TYPES_TYPE;
  };
  HostGame: { userName?: string; gameType: GAME_TYPES_TYPE };
  JoinGame: { userName?: string };
  JoinCode: { joinCode: string; userName: string };
  PassAndPlay: { gameType: GAME_TYPES_TYPE };
  Game: {
    game: GameType;
    mode: GameMode;
    difficulty?: AIDifficultyLevel;
    userId?: string;
    journey?: boolean;
    bossUnlocked?: boolean;
  };
  Splash: undefined;
  WebOnly: undefined;
  ContactUs: undefined;
  Subscriptions: undefined;
  Journey: undefined;
};

const Stack = createNativeStackNavigator<RootStackParamList>();
const navRef = createNavigationContainerRef<RootStackParamList>();

export const getRootNavRef = () => {
  return navRef.isReady() ? navRef : undefined;
};

const config: LinkingOptions<RootStackParamList>['config'] = {
  screens: {
    ChooseUserType: 'chooseUserType',
    VerifyEmail: 'verifyEmail',
    SignUp: 'signUp',
    SignIn: 'signIn',
    RequestForgetPasswordCode: 'requestForgetPasswordCode',
    VerifyForgetPasswordCode: 'verifyForgetPasswordCode',
    ResetPassword: 'resetPassword',
    ResetPasswordSuccessful: 'resetPasswordSuccessful',
    MyHives: 'myHives',
    MyHiveData: 'myHiveData',
    MyHiveDataStudent: 'myHiveDataStudent',
    CreateHive: 'createHive',
    SelectGameType: 'selectGameType',
    SelectHiveSize: 'selectHiveSize',
    OrderSummary: 'orderSummary',
    JoinHive: 'joinHive',
    WaitingForHiveMate: 'waitingForHiveMate',
    Lobby: 'lobby',
    LobbyJourney: 'lobby/journey',
    JourneySurvey: 'lobby/journey/survey',
    JourneyAssessment: 'lobby/journey/assessment',
    GymAssessment: 'lobby/gym/assessment',
    GymStats: 'lobby/gym/stats',
    NectarNook: 'nectarNook',
    HiveJourneys: 'hiveJourneys',
    HiveSettings: 'hiveSettings',
    HiveReports: 'hiveReports',
    PlayOnline: 'playOnline',
    ServerRedirect: 'serverRedirect',
    HostGame: 'hostGame',
    JoinGame: 'joinGame',
    PlayWithAI: 'playWithAI',
    JoinCode: 'joinCode',
    PassAndPlay: 'passAndPlay',
    Game: {
      path: 'game',
      stringify: isMobileApp ? undefined : { game: utils.encodeParam },
    },
    Splash: 'splash',
    ContactUs: 'contactUs',
    Subscriptions: 'subscriptions',
    Journey: 'journey',
    AdminNavigator: {
      path: 'admin',
      screens: {
        Teachers: 'teachers',
        Students: 'students',
      },
    },
    WebOnly: 'webOnly',
  },
};

const linking = {
  prefixes: [envs.WEBSITE_BASE_URL, envs.MOBILE_DEEP_LINK],
  config,
  getStateFromPath: isMobileApp
    ? undefined
    : (path: string, options: any) =>
        path.includes('/serverRedirect')
          ? getStateFromPath(path, options)
          : utils.decodeParams(getStateFromPath(path, options)),
};

const RootNavigation = () => {
  useGetCurrentUserData();
  const [showSplash, setShowSplash] = useState(true);
  const [isVersionChecked, setVersionChecked] = useState(Platform.OS === 'web');
  const { isSignedIn, signedInStatus, setSignedInStatus, user } = useSignedInContext();

  const mobileNotificationSubscription = useRef<TMB_EventSubscription>();
  const [isReady, setReady] = useState(false);
  const [updatePushToken] = useAddFirebaseDeviceToken();
  const [invitedGameDetail, setInvitedGameDetail] = useState(undefined);

  useEffect(() => {
    setTimeout(() => {
      setShowSplash(false);
    }, 2000);
  }, []);

  useEffect(() => {
    if (signedInStatus === SIGNED_IN_STATUS.loading && isVersionChecked) {
      MB_accessTokenUtils.getAccessToken().then(accessToken => {
        setSignedInStatus(accessToken ? SIGNED_IN_STATUS.signedIn : SIGNED_IN_STATUS.signedOut);
      });
    }
  }, [isReady, isVersionChecked, setSignedInStatus, signedInStatus]);

  useEffect(() => {
    if (signedInStatus === SIGNED_IN_STATUS.signedIn) {
      mobileNotificationSubscription.current = MB_EventEmmiter.addListener(
        MB_EVENT_EMMITER_EVENT_TYPE.message,
        event => {
          if (
            event.origin !== envs.WEBSITE_BASE_URL ||
            event.data.message !== 'NOTIFICATION_UPDATE'
          ) {
            return;
          }
          setInvitedGameDetail(event.data.payload.data);
        },
      );
    } else if (signedInStatus === SIGNED_IN_STATUS.signedOut) {
      mobileNotificationSubscription.current?.remove();
      mobileNotificationSubscription.current = undefined;
    }
  }, [signedInStatus]);

  useEffect(() => {
    if (isMobileApp) {
      const unSubscribe = MB_FirebaseMessaging.onNotificationPressed(event => {
        if (event.data) {
          setTimeout(() => {
            setInvitedGameDetail(event.data);
          }, 1000);
        }
      });
      return unSubscribe;
    }
  }, []);

  useEffect(() => {
    if (navRef.isReady() && !isSignedIn) {
      const currentRoute = navRef.getCurrentRoute();
      if (currentRoute?.name !== 'SignIn') {
        navRef.reset({ index: 0, routes: [{ name: 'SignIn' }] });
      }
    }
  }, [isSignedIn]);

  useEffect(() => {
    if (signedInStatus === SIGNED_IN_STATUS.signedIn) {
      (async () => {
        try {
          const token = await MB_FirebaseMessaging.getToken();
          updatePushToken({ variables: { token } }).catch(err =>
            console.warn('Error adding firebase token', err),
          );
        } catch (error) {
          console.log('Failure to register device token', error);
        }
      })();
    }
  }, [signedInStatus, updatePushToken]);

  useEffect(() => {
    setReady(true);
  }, [isSignedIn]);

  const renderGuestRoutes = () =>
    !isSignedIn && (
      <>
        <Stack.Screen name="ChooseUserType" component={ChooseUserType} />
        <Stack.Screen name="VerifyEmail" component={VerifyEmail} />
        <Stack.Screen name="SignIn" component={SignIn} />
        <Stack.Screen name="SignUp" component={SignUp} />
        <Stack.Screen name="RequestForgetPasswordCode" component={RequestForgetPasswordCode} />
        <Stack.Screen name="VerifyForgetPasswordCode" component={VerifyForgetPasswordCode} />
        <Stack.Screen name="ResetPassword" component={ResetPassword} />
        <Stack.Screen name="ResetPasswordSuccessful" component={ResetPasswordSuccessful} />
      </>
    );

  const renderPrivateRoutes = () =>
    isSignedIn && (
      <>
        <Stack.Screen name="MyHives" component={MyHives} />
        <Stack.Screen name="JoinHive" component={JoinHive} />
        <Stack.Screen name="CreateHive" component={CreateHive} />
        <Stack.Screen name="SelectHiveSize" component={SelectHiveSize} />
        <Stack.Screen name="OrderSummary" component={OrderSummary} />
        <Stack.Screen name="MyHiveData" component={MyHiveData} />
        <Stack.Screen name="MyHiveDataStudent" component={MyHiveDataStudent} />
        <Stack.Screen name="WaitingForHiveMate" component={WaitingForHiveMate} />
      </>
    );

  const renderMainRoutes = () => (
    <NavigationContainer ref={navRef} linking={linking}>
      <Stack.Navigator
        initialRouteName={isSignedIn ? 'Lobby' : 'SignIn'}
        screenOptions={{
          headerShown: true,
          headerBackTitle: '',
          title: 'Number Hive',
          header: props => <Header route={props.route} />,
        }}
      >
        <Stack.Screen name="Lobby" component={Lobby} />
        <Stack.Screen name="LobbyJourney" component={LobbyJourney} />
        <Stack.Screen name="JourneySurvey" component={JourneySurvey} />
        <Stack.Screen name="JourneyAssessment" component={JourneyAssessment} />
        <Stack.Screen name="GymAssessment" component={GymAssessment} />
        <Stack.Screen name="GymStats" component={GymStats} />
        <Stack.Screen name="NectarNook" component={NectarNookScreen} />
        <Stack.Screen name="HiveJourneys" component={HiveJourneys} />
        <Stack.Screen name="HiveSettings" component={HiveSettings} />
        <Stack.Screen name="HiveReports" component={HiveReports} />
        <Stack.Screen name="SelectGameType" component={SelectGameType} />
        <Stack.Screen name="PlayOnline" component={PlayOnline} />
        <Stack.Screen name="HostGame" component={HostGame} />
        <Stack.Screen name="JoinGame" component={JoinGame} />
        <Stack.Screen name="PlayWithAI" component={PlayWithAI} />
        <Stack.Screen name="JoinCode" component={JoinCode} />
        <Stack.Screen name="PassAndPlay" component={PassAndPlay} />
        <Stack.Screen name="Game" component={Game} />
        <Stack.Screen name="ContactUs" component={ContactUs} />
        <Stack.Screen name="Subscriptions" component={Subscriptions} />
        {renderGuestRoutes()}
        {renderPrivateRoutes()}
        <Stack.Screen name="ServerRedirect" component={ServerRedirect} />
      </Stack.Navigator>
      {invitedGameDetail && (
        <InviteToGame
          detail={invitedGameDetail}
          onYesPress={() => setInvitedGameDetail(undefined)}
          onNoPress={() => setInvitedGameDetail(undefined)}
        />
      )}
      {Platform.OS === 'web' && (envs.ENV === 'development' || envs.ENV === 'custom') && (
        <View style={{ position: 'fixed' as any, bottom: 8, left: 8, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 4, paddingHorizontal: 8, paddingVertical: 3 }}>
          <Text style={{ color: '#fff', fontSize: 11 }}>{gitBranch}</Text>
        </View>
      )}
    </NavigationContainer>
  );

  const renderSplash = () => (
    <>
      <ForceUpdate setVersionChecked={setVersionChecked} />
      <NavigationContainer>
        <Stack.Navigator screenOptions={{ headerShown: false }}>
          <Stack.Screen name="Splash" component={Splash} />
        </Stack.Navigator>
      </NavigationContainer>
    </>
  );

  const renderAdminRoutes = () => (
    <NavigationContainer ref={navRef} linking={linking}>
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        {isMobile ? (
          <Stack.Screen name="WebOnly" component={WebOnly} />
        ) : (
          <Stack.Screen name="AdminNavigator" component={AdminNavigator} />
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );

  if (showSplash || !isVersionChecked) return renderSplash();
  if (isSignedIn && user?.userType === USER_TYPES.admin) return renderAdminRoutes();
  if (!isSignedIn) {
    return (
      <NavigationContainer ref={navRef} linking={linking}>
        <Stack.Navigator initialRouteName="SignIn" screenOptions={{ headerShown: false }}>
          <Stack.Screen name="SignIn" component={SignIn} />
          <Stack.Screen name="SignUp" component={SignUp} />
          <Stack.Screen name="RequestForgetPasswordCode" component={RequestForgetPasswordCode} />
          <Stack.Screen name="VerifyForgetPasswordCode" component={VerifyForgetPasswordCode} />
          <Stack.Screen name="ResetPassword" component={ResetPassword} />
          <Stack.Screen name="ResetPasswordSuccessful" component={ResetPasswordSuccessful} />
          <Stack.Screen name="ChooseUserType" component={ChooseUserType} />
          <Stack.Screen name="VerifyEmail" component={VerifyEmail} />
        </Stack.Navigator>
      </NavigationContainer>
    );
  }

  return renderMainRoutes();
};

export default RootNavigation;
